import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';

export * from './publishForumReplyApi';
export * from './toggleForumReplyFlagApi';

// ============================================================================
// Forum Document Schemas
// ============================================================================
// Schemas for forum post and reply documents stored at
// `forumData/{courseId}/forums/{forumId}/{posts|replies}/{docId}`.
//
// SDK-agnostic: time fields use FirestoreTimestampSchema so the admin/ and
// client/ layers can extend with their own Timestamp transforms.
// ============================================================================

/** Shared fields across forum post and reply documents. */
export const sharedForumDataModel = z.object({
  /**
   * Display name of the author at write time. May be missing on legacy docs.
   *
   * `null` is tolerated because the Firebase Auth `User.displayName` is
   * `string | null` and historical client code wrote it through unchanged.
   * The trigger does not consume this field — it's display-only — so the
   * schema must not gate notification delivery on its shape. See #2953.
   */
  author: z.string().nullish(),
  /** Firebase Auth uid of the author. */
  authorUid: z.string().optional(),
  /** Body of the post / reply. */
  contents: z
    .object({
      html: z.string().optional(),
      text: z.string().optional(),
    })
    .optional(),
});

/** Minimal post shape for notification emails. Incomplete – full post doc has more. */
export const ForumPostDataSchema = sharedForumDataModel.extend({
  id: z.string().optional(),
  title: z.string().optional(),
  /**
   * Whether the author posted anonymously. The stored `author` and
   * `authorUid` stay real, so notifications must hide them themselves.
   */
  isAnonymous: z.boolean().optional(),
});

export type ForumPostData = z.infer<typeof ForumPostDataSchema>;

/**
 * Forum reply document schema.
 *
 * Required fields are the ones the `onForumReply` trigger relies on to do its
 * job (publish detection, parent-author lookup, notification email
 * construction). Other fields are written by the client on create but kept
 * optional here so that legacy reply documents (which may predate one or
 * another field) still parse cleanly.
 *
 * The schema is `.loose()` so undocumented fields (e.g. `notificationsSent`,
 * trigger-side audit flags) pass through unchanged. Notably, `postUrl` is
 * deliberately absent: the URL is derived server-side from
 * `(courseId, forumId, postId)` via `buildForumPostUrl` — it must never be
 * trusted from the document.
 */
export const ForumReplyDataSchema = sharedForumDataModel
  .extend({
    /** Stable UUID of the reply, mirrored as the doc id. */
    id: z.string().optional(),
    /** Firebase Auth uid of the author. Required — used to attribute the email. */
    authorUid: z.string(),
    /** Doc id of the parent (post or reply). Required — used to find the recipient. */
    parent: z.string(),
    /** Doc id of the root post. Required — used to build the notification URL. */
    postId: z.string(),
    /** True while in the editor; flipped to false on publish. Required. */
    isDraft: z.boolean(),
    /** Time of creation (Firestore Timestamp). */
    time: FirestoreTimestampSchema.optional(),
    /**
     * Time the reply was published, stamped by `publishforumreply` in the
     * same write that flips `isDraft` to false. Distinct from `time`, which is set
     * once at draft creation and so lags publish for slow drafters. The
     * notification freshness window is measured against this field, not
     * `time`, so a reply composed over many minutes still notifies on
     * publish. Absent on legacy replies published before this field existed.
     */
    publishedAt: FirestoreTimestampSchema.optional(),
    /**
     * Whether the reply is hidden as moderated: set by `publishforumreply`
     * from the moderation check, or by staff through `toggleforumreplyflag`.
     */
    isFlagged: z.boolean().optional(),
    /**
     * Set while staff hold a flag on the reply, so the author's republish
     * keeps it flagged. Only `toggleforumreplyflag` changes it.
     */
    flaggedByStaff: z.boolean().optional(),
    /** Endorsement flag. */
    isEndorsed: z.boolean().optional(),
    /** Set by the trigger after the notification email is queued. */
    notificationsSent: z.boolean().optional(),
    /** Map of uid -> liked. */
    likedBy: z.record(z.string(), z.boolean()).optional(),
    /**
     * DocumentReference to the published TipTap doc. Stored as a Firestore
     * reference; we don't model its shape here.
     */
    tipTapDoc: z.unknown().optional(),
  })
  .loose();

export type ForumReplyData = z.infer<typeof ForumReplyDataSchema>;

// ============================================================================
// Paths
// ============================================================================

export const FORUM_DATA_COLLECTION = 'forumData' as const;

/**
 * Collection path for replies under a given course/forum:
 * `forumData/{courseId}/forums/{forumId}/replies`.
 *
 * @param params.courseId - Course identifier; first path segment.
 * @param params.forumId - Forum identifier; second path segment.
 * @return Firestore collection path string suitable for `firestore.collection(...)`.
 */
export function getForumReplyCollectionPath({
  courseId,
  forumId,
}: {
  courseId: string;
  forumId: string;
}): string {
  return `${FORUM_DATA_COLLECTION}/${courseId}/forums/${forumId}/replies`;
}

/**
 * Document path for a single reply:
 * `forumData/{courseId}/forums/{forumId}/replies/{replyId}`.
 *
 * @param params.courseId - Course identifier.
 * @param params.forumId - Forum identifier.
 * @param params.replyId - Reply doc identifier.
 * @return Firestore document path string suitable for `firestore.doc(...)`.
 */
export function getForumReplyDocumentPath({
  courseId,
  forumId,
  replyId,
}: {
  courseId: string;
  forumId: string;
  replyId: string;
}): string {
  return `${getForumReplyCollectionPath({courseId, forumId})}/${replyId}`;
}
